// api-client.ts — 19BL Session 3a · shared v4 HTTP helpers + error types.
//
// Why this file exists (extracted from api/ingest.ts):
//   postV4 / getV4 were private to ingest.ts, so every new API client (repair,
//   telemetry, future ones) either re-implemented the fetch+validate pattern
//   or skipped validation entirely. Centralizing the helpers here lets later
//   sessions (3c) add cross-cutting concerns ONCE — telemetry-on-error,
//   timeout, retry — instead of N times.
//
// What was MOVED here (and why it's safe — see cross-check agent report):
//   - IngestApiError + IngestErrorResponse: DEFINED here now, re-exported by
//     ingest.ts. Tests import IngestApiError from ingest.ts and rely on
//     `instanceof` (friendly-error.ts, use-regen-action.ts, repair-dialog.tsx);
//     the re-export keeps those import paths + identities intact. The vi.mock
//     in use-regen-action.test.tsx uses `...actual` so the re-exported class
//     is still picked up. Dependency direction is one-way (ingest -> api-client)
//     to avoid a cycle.
//   - postV4 / getV4: moved verbatim. ingest.ts now imports them from here;
//     its 9 exported v4 functions call these unchanged.
//
// What was NOT moved (per cross-check — would break things):
//   - staged-ingest-client.ts (SSE getReader streaming + workflow polling —
//     cannot be unified under a JSON-fetch helper)
//   - telemetry.ts (never-throws contract + keepalive + empty-default-url)
//   - the error-class CONSTRUCTORS' field shapes (instanceof-locked)

import type { z } from 'zod';
import { IngestContractError } from '@examforge/shared/errors';
import { generateErrorId } from './error-id.js';
import { getOrCreateAnonId } from '../auth/anon-id.js';

/**
 * Base URL for the ingest worker. Single definition now (was duplicated in
 * ingest.ts / repair.ts / staged-ingest-client.ts — Session 3c collapses the
 * other two onto this one; for now this file + ingest.ts agree because ingest
 * imports the constant from here).
 */
export const INGEST_URL: string =
  (import.meta.env.VITE_INGEST_URL as string | undefined) ?? 'http://localhost:8787';

/**
 * 19BN Phase 0: build the standard header set for any AI-bound request.
 *
 * Backend reads ONLY the X-Anon-Id header (ingest/src/api/helpers.ts:42
 * getAnonId). Body `anonId` fields exist purely to satisfy zod validation on
 * 4 regen endpoints — handlers discard them. So the header is the source of
 * truth for anonId on the wire.
 *
 * Centralizing here means every v4 caller (postV4/getV4 → 9 endpoints) plus
 * any raw fetch that builds on this helper gets the real anonId for free,
 * closing the Phase 0 gap where 5 of 7 AI call sites were sending 'unknown'.
 */
export function ingestHeaders(extra?: Record<string, string>): Record<string, string> {
  return {
    'Content-Type': 'application/json',
    'X-Anon-Id': getOrCreateAnonId(),
    // 19BO+ CROSS-ORIGIN-AUTH: CSRF defense header. The backend rejects
    // state-changing methods (POST/PUT/PATCH/DELETE) without this. Harmless
    // on GET (the backend exempts read-only methods). See
    // ingest/src/middleware/csrf-header.ts.
    'X-Requested-With': 'fetch',
    ...extra,
  };
}

/**
 * 19BO+ CROSS-ORIGIN-AUTH: header set for credentialed account/auth requests
 * (balance, recharge, subscription, profile, notifications, sync). Like
 * ingestHeaders but WITHOUT X-Anon-Id — these are user-scoped (cookie-auth)
 * endpoints, not anonymous AI-ingest calls. Includes X-Requested-With for the
 * CSRF gate (harmless on GET). Callers add `credentials: 'include'` per-call.
 */
export function accountHeaders(extra?: Record<string, string>): Record<string, string> {
  return {
    'Content-Type': 'application/json',
    'X-Requested-With': 'fetch',
    ...extra,
  };
}

/** Error body shape returned by v4 endpoints on failure. */
export interface IngestErrorResponse {
  error: string;
  message?: string;
  schemaName?: string;
  issues?: Array<{ path: string; message: string }>;
}

/**
 * Thrown by postV4/getV4 on a non-2xx response. Carries the parsed error body
 * so callers (friendly-error, use-regen-action) can read `.response.error` /
 * `.response.message` and branch on specific codes (e.g. workflow_not_bound).
 *
 * Defined here (not ingest.ts) so the dependency graph is one-way, but
 * re-exported by ingest.ts to preserve every existing import path + the
 * `instanceof IngestApiError` checks across the codebase.
 */
export class IngestApiError extends Error {
  override readonly name = 'IngestApiError';
  constructor(
    public readonly status: number,
    public readonly response: IngestErrorResponse,
  ) {
    super(response.message ?? response.error);
  }
}

/**
 * 19BL Session 3c: report an API error to telemetry, then rethrow. Single
 * chokepoint — every postV4/getV4 HTTP failure routes here, so a future catch
 * site can't silently skip reporting (the gap the cross-check found: most catch
 * sites setError/toast and never call recordEvent).
 *
 * Contract:
 *   - ALWAYS rethrows `err` (telemetry must never swallow the user-facing error)
 *   - reporting is fire-and-forget (a recordEvent rejection is caught locally,
 *     never propagated — the rethrow below uses the ORIGINAL error)
 *   - dynamic-imports recordEvent to avoid a static cycle (idb.ts -> telemetry,
 *     api-client -> idb would otherwise loop at module-init)
 *
 * The ErrorDiagnosis packet is built here so admin/agent (Session 5) gets
 * httpStatus / url / method / code without re-deriving them.
 */
async function reportApiError(
  err: IngestApiError,
  method: 'POST' | 'GET',
  path: string,
): Promise<void> {
  const errorId = generateErrorId();
  const diagnosis = {
    errorId,
    code: err.response.error,
    httpStatus: err.status,
    url: path,
    method,
    occurredAt: Date.now(),
    ...(typeof __APP_VERSION__ !== 'undefined' ? { appVersion: __APP_VERSION__ } : {}),
  };
  try {
    const { recordEvent } = await import('../storage/idb.js');
    // recordEvent stamps errorId into payload + pushes to D1 with id=errorId
    // (Session 2). We pass type='api-error' (in ERROR_EVENT_TYPES) so it's
    // treated as an error and gets the id + joins the future error feed.
    void recordEvent({
      type: 'api-error',
      summary: `${method} ${path} → ${err.status} ${err.response.error}`.slice(0, 500),
      viewPath: typeof window !== 'undefined' ? window.location.pathname : undefined,
      payload: diagnosis,
    });
  } catch {
    // Telemetry must never break the error path. The rethrow below proceeds
    // with the original error regardless.
  }
}

/**
 * Generic POST helper for v4 endpoints. Validates the response against `schema`.
 * Throws IngestApiError on HTTP error, IngestContractError on shape mismatch.
 *
 * 19BL Session 3c: on HTTP error, ALSO reports an api-error event to telemetry
 * (single chokepoint — covers every v4 caller) before rethrowing. Schema
 * mismatches (IngestContractError) are NOT reported here — they're FE/BE contract
 * drift, a different signal, not an API failure.
 */
export async function postV4<T>(path: string, body: unknown, schema: z.ZodType<T>): Promise<T> {
  const res = await fetch(`${INGEST_URL}${path}`, {
    method: 'POST',
    headers: ingestHeaders(),
    body: JSON.stringify(body),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new IngestApiError(res.status, json as IngestErrorResponse);
    await reportApiError(err, 'POST', path);
    throw err;
  }
  const parsed = schema.safeParse(json);
  if (!parsed.success) {
    throw new IngestContractError(res.status, parsed.error, json);
  }
  return parsed.data;
}

/**
 * Generic GET helper for v4 endpoints. Validates the response against `schema`.
 * Throws IngestApiError on HTTP error, IngestContractError on shape mismatch.
 * Reports api-error on HTTP failure (same contract as postV4).
 */
export async function getV4<T>(path: string, schema: z.ZodType<T>): Promise<T> {
  const res = await fetch(`${INGEST_URL}${path}`, {
    method: 'GET',
    headers: ingestHeaders(),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new IngestApiError(res.status, json as IngestErrorResponse);
    await reportApiError(err, 'GET', path);
    throw err;
  }
  const parsed = schema.safeParse(json);
  if (!parsed.success) {
    throw new IngestContractError(res.status, parsed.error, json);
  }
  return parsed.data;
}
